/**
 * 1日分の睡眠の指標を、普段（直近 30 日の1日あたりの中央値）との差と並べて表示する
 */

import type { SleepDayContext, SleepDayMetrics } from '@/api/types/sleep'
import { cn } from '@/lib/utils'

import { formatMinutes, formatSigned } from '../format'

type Better = 'higher' | 'lower' | null

type Item = {
  label: string
  value: string
  sub?: string
  diffKey?: string
  unit?: string
  better: Better
}

type Props = {
  metrics: SleepDayMetrics
  context: SleepDayContext
}

const fmt = (v: number | null, digits = 0, unit = ''): string => (v === null ? '--' : `${v.toFixed(digits)}${unit}`)

const buildItems = (m: SleepDayMetrics): Item[] => [
  {
    label: '合計睡眠',
    value: formatMinutes(m.total_min),
    sub: m.nap_min > 0 ? `うち昼寝 ${formatMinutes(m.nap_min)}` : `寝床 ${formatMinutes(m.in_bed_min)}`,
    diffKey: 'total_min',
    unit: '分',
    better: 'higher',
  },
  {
    label: '睡眠の回数',
    value: `${m.episode_count}回`,
    sub: m.episode_count > 1 ? `最長 ${formatMinutes(m.longest_min)}` : undefined,
    diffKey: 'episode_count',
    better: null,
  },
  {
    label: '睡眠効率',
    value: fmt(m.efficiency_pct, 0, '%'),
    diffKey: 'efficiency_pct',
    unit: 'pt',
    better: 'higher',
  },
  {
    label: '深い睡眠',
    value: fmt(m.deep_pct, 0, '%'),
    sub: m.deep_min !== null ? formatMinutes(m.deep_min) : undefined,
    diffKey: 'deep_pct',
    unit: 'pt',
    better: 'higher',
  },
  {
    label: 'REM',
    value: fmt(m.rem_pct, 0, '%'),
    sub: m.rem_min !== null ? formatMinutes(m.rem_min) : undefined,
    diffKey: 'rem_pct',
    unit: 'pt',
    better: 'higher',
  },
  {
    label: '覚醒',
    value: formatMinutes(m.awake_min),
    diffKey: 'awake_min',
    unit: '分',
    better: 'lower',
  },
  {
    label: '最低心拍',
    value: fmt(m.hr_min, 0, ' bpm'),
    diffKey: 'hr_min',
    better: 'lower',
  },
  {
    label: 'HRV',
    value: fmt(m.hrv_avg, 0, ' ms'),
    sub: m.hrv_status ?? undefined,
    diffKey: 'hrv_avg',
    unit: 'ms',
    better: 'higher',
  },
  {
    label: '安静時心拍',
    value: fmt(m.resting_hr, 0, ' bpm'),
    diffKey: 'resting_hr',
    better: 'lower',
  },
  {
    label: 'Body Battery',
    value: m.bb_gain !== null ? formatSigned(m.bb_gain) : '--',
    sub: m.bb_end !== null ? `最後の起床時 ${m.bb_end}` : undefined,
    diffKey: 'bb_gain',
    better: 'higher',
  },
  {
    label: '睡眠中ストレス',
    value: fmt(m.stress_avg, 0),
    diffKey: 'stress_avg',
    better: 'lower',
  },
  {
    label: 'SpO2 平均',
    value: fmt(m.spo2_avg, 0, '%'),
    diffKey: 'spo2_avg',
    unit: 'pt',
    better: 'higher',
  },
  {
    label: 'SpO2 最低',
    value: fmt(m.spo2_low, 0, '%'),
    sub: m.breathing_disruption ? `呼吸の乱れ ${m.breathing_disruption}` : undefined,
    better: null,
  },
]

const DiffBadge = ({ diff, better, unit }: { diff: number; better: Better; unit?: string }) => {
  if (Math.abs(diff) < 0.5) return <span className="text-[11px] text-muted-foreground">普段どおり</span>
  const good = better === 'higher' ? diff > 0 : better === 'lower' ? diff < 0 : null
  return (
    <span
      className={cn(
        'text-[11px] tabular-nums',
        good === true && 'text-emerald-600 dark:text-emerald-400',
        good === false && 'text-rose-600 dark:text-rose-400',
      )}
    >
      普段比 {formatSigned(diff)}
      {unit}
    </span>
  )
}

export const MetricGrid = ({ metrics, context }: Props) => {
  const deviations = new Map(Object.entries(context.deviations))
  const items = buildItems(metrics).filter((item) => item.value !== '--')

  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {items.map((item) => {
          const diff = item.diffKey ? deviations.get(item.diffKey) : undefined
          return (
            <div key={item.label} className="rounded-lg border bg-card p-3">
              <p className="text-[11px] text-muted-foreground">{item.label}</p>
              <p className="text-lg font-semibold tabular-nums">{item.value}</p>
              {item.sub && <p className="text-[11px] text-muted-foreground">{item.sub}</p>}
              {diff !== undefined && <DiffBadge diff={diff} better={item.better} unit={item.unit} />}
            </div>
          )
        })}
      </div>
      {context.baseline.n > 0 && (
        <p className="text-[11px] text-muted-foreground">
          普段 = 直近30日のうち睡眠のあった {context.baseline.n}日の中央値。深い睡眠・REM は主睡眠に占める割合
        </p>
      )}
    </section>
  )
}
